import { supabase } from "../../config/supabase";
import type { AuthError, User } from "@supabase/supabase-js";
import type { PostgrestError } from "@supabase/supabase-js";

export type AppError = AuthError | PostgrestError;

export interface AuthResponse {
  user: User | null;
  error: AppError | null;
}

export const auth = {
  /**
   * Sign up a new user
   */
  async signUp(
    email: string,
    password: string,
    userData: {
      user_name: string;
      owner?: string;
      description?: string;
      cell?: string;
    }
  ): Promise<AuthResponse> {
    const {
      data: { user },
      error,
    } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData,
      },
    });

    if (error) {
      return { user: null, error };
    }

    // Create user record in users table
    if (user) {
      const { error: dbError } = await supabase.from("users").insert([
        {
          id: user.id,
          user_name: userData.user_name,
          email: email,
          owner: userData.owner,
          description: userData.description,
          cell: userData.cell,
          status: "active",
        },
      ]);

      if (dbError) {
        return { user: null, error: dbError };
      }
    }

    return { user, error: null };
  },

  /**
   * Sign in a user
   */
  async signIn(email: string, password: string): Promise<AuthResponse> {
    const {
      data: { user },
      error,
    } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { user, error };
  },

  /**
   * Sign out the current user
   */
  async signOut(): Promise<{ error: AppError | null }> {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  /**
   * Get the current user
   */
  async getCurrentUser(): Promise<{
    user: User | null;
    error: AppError | null;
  }> {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    return { user, error };
  },

  /**
   * Update the current user's profile
   */
  async updateProfile(userData: {
    email?: string;
    user_name?: string;
    owner?: string;
    description?: string;
    cell?: string;
  }): Promise<{ error: AppError | null }> {
    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError || !user) {
      return { error: authError };
    }

    const { error: dbError } = await supabase
      .from("users")
      .update(userData)
      .eq("id", user.id);

    return { error: dbError };
  },

  /**
   * Get current user with role information
   */
  async getCurrentUserWithRole(): Promise<{
    user: User | null;
    userProfile: any | null;
    error: AppError | null;
  }> {
    const { user, error } = await this.getCurrentUser();

    if (error || !user) {
      return { user: null, userProfile: null, error };
    }

    // Get user profile with role from public.users table
    const { data: userProfile, error: profileError } = await supabase
      .from("users")
      .select("*")
      .eq("id", user.id)
      .single();

    return {
      user,
      userProfile,
      error: profileError,
    };
  },

  /**
   * Update user profile information
   */
  async updateUserProfile(
    userId: string,
    updates: {
      display_name?: string;
      description?: string;
      phone_number?: string;
    }
  ): Promise<{
    data: any | null;
    error: AppError | null;
  }> {
    const { data, error } = await supabase
      .from("users")
      .update(updates)
      .eq("id", userId)
      .select()
      .single();

    return { data, error };
  },
};
